import type { AxiosRequestConfig } from '@ahm/api-wrappers-core';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { NearestBranch } from './ahafood';
import { apiRoutes } from './api-routes';
import type { Branch } from './use-get-store-order';
import { webviewHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';

export interface ProgressDeal {
  progress: Progress[];
}

export interface Progress {
  page_id: number;
  ai_tags: string[];
  branches: Branch[];
  item_id: number;
  item_name: string;
  store_id: number;
  store_name: string;
  store_open_hours: OpenHoursType;
  promo_code: string;
  promo_name: string;
  promo_desc: string;
  store_slug: string;
  discount_config: DiscountConfig;
  max_use: number;
  usage_count: number;
  status: string;
  city_id: string;
  item_image_url: string;
  store_image_url: string;
  total_items: number;
  price: number;
  discounted_price: number;
  nearest_branch?: NearestBranch;
  distance?: number;
}

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

export interface TimeSlot {
  start_time: number;
  end_time: number;
}

export interface DiscountConfig {
  type: string;
  value: number;
  max_discount_value: number;
  max_apply_per_item: number;
}

export interface DealProgressParams {
  city_id: string;
  skip?: number;
  limit?: number;
  reach_max_use?: boolean;
  lat?: number;
  lng?: number;
}

export const getDealProgressFn = async (
  params: DealProgressParams,
  config: AxiosRequestConfig = {}
): Promise<ProgressDeal> => {
  const response = await webviewHttpClient.get<ProgressDeal>(
    apiRoutes.ahafood.web_view.get_deal_progress,
    {
      ...config,
      params: {
        city_id: params.city_id,
        skip: params.skip ?? 0,
        limit: params.limit ?? 20,
        reach_max_use: params.reach_max_use ?? false,
        ...(params.lat &&
          params.lng && {
            lat: params.lat,
            lng: params.lng,
          }),
      },
    }
  );

  return response?.data;
};

export const useGetDealProgress = (
  params: DealProgressParams,
  opts?: Omit<UseQueryOptions<ProgressDeal, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<ProgressDeal, Error>({
    queryKey: [apiRoutes.ahafood.web_view.get_deal_progress, params],
    queryFn: () => getDealProgressFn(params),
    ...opts,
  });
};
